/**
 * Workspace subdomain routing: `<subdomain>.<base>` (e.g. riverbend.graphit.io)
 * names a workspace, the same way the backend's TenantContextMiddleware reads
 * the host when no header or cookie names one.
 */
import { TENANT_DOMAIN_BASE } from './subdomain'

/** Hosts under the base domain that are the product itself, never a workspace. */
const RESERVED_LABELS = new Set(['app', 'www', 'api', 'docs'])

/** One DNS label: letters, digits and inner hyphens. */
const LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/

/**
 * The workspace a hostname names, or null for any other host.
 *
 * Only exactly one label directly under the base counts: the bare base,
 * reserved hosts (app, www, api, docs), deeper labels, localhost, IP
 * addresses and other domains are not workspaces.
 *
 * @param {string} [hostname] - defaults to the current page's host
 * @param {string} [base] - the tenant domain base, e.g. "graphit.io"
 * @returns {string|null} the lowercase subdomain
 */
export function tenantSubdomainFromHost(
  hostname = window.location.hostname,
  base = TENANT_DOMAIN_BASE,
) {
  if (typeof hostname !== 'string' || typeof base !== 'string' || !base) return null
  const host = hostname.toLowerCase()
  const suffix = `.${base.toLowerCase()}`
  if (!host.endsWith(suffix)) return null
  const label = host.slice(0, -suffix.length)
  if (!LABEL_PATTERN.test(label) || RESERVED_LABELS.has(label)) return null
  return label
}

/**
 * The user's membership for a workspace subdomain.
 *
 * @param {Array<{subdomain?: string}>} tenants - the signed-in user's tenants
 * @param {string|null} subdomain
 * @returns {Object|null}
 */
export function findTenantBySubdomain(tenants, subdomain) {
  if (!subdomain || !Array.isArray(tenants)) return null
  const wanted = subdomain.toLowerCase()
  return tenants.find((tenant) => String(tenant?.subdomain || '').toLowerCase() === wanted) || null
}

/**
 * The URL of a path on a workspace's own host, on the current scheme and port.
 *
 * @param {string} subdomain
 * @param {string} path - absolute path, e.g. "/dashboards"
 * @param {{protocol?: string, port?: string, base?: string}} [origin]
 * @returns {string|null} null when the subdomain would not name a workspace host
 */
export function workspaceHostUrl(subdomain, path, {
  protocol = window.location.protocol,
  port = window.location.port,
  base = TENANT_DOMAIN_BASE,
} = {}) {
  const host = `${String(subdomain || '').toLowerCase()}.${base}`
  if (!tenantSubdomainFromHost(host, base)) return null
  return `${protocol}//${host}${port ? `:${port}` : ''}${path}`
}
